import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ScrollText, List, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";

function renderBody(body) {
  const lines = (body || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) { blocks.push({ type: "ul", items: list }); list = []; } };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (line.startsWith("- ")) list.push(line.slice(2));
    else { flush(); blocks.push({ type: "p", text: line }); }
  });
  flush();
  return blocks;
}

export default function TermsOfUse() {
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [active, setActive] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const obsRef = useRef(null);
  useSeo("/terms-of-use");

  useEffect(() => {
    api.get("/public/terms-of-use").then((r) => setPage(r.data)).catch(() => setPage({ error: true }));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings || {})).catch(() => {});
  }, []);

  const sections = page?.sections || [];

  useEffect(() => {
    if (!sections.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-15% 0px -75% 0px", threshold: 0 });
    document.querySelectorAll("[data-toc-section]").forEach((el) => obs.observe(el));
    obsRef.current = obs;
    return () => obs.disconnect();
  }, [sections.length]);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setTocOpen(false); }
  };

  if (!page) return <div className="min-h-screen" />;
  const hero = page.hero || {};

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header phone={settings.phone} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="container-cz pt-8 pb-12 sm:pt-10 sm:pb-16">
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="terms-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
            <span className="text-slate-700 font-medium">Terms of Use</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary bg-sky-50 rounded-full px-3 py-1.5">
              <ScrollText className="w-3.5 h-3.5" /> {hero.eyebrow || "Legal"}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#16161a] mt-5">
              {hero.title || "Terms of Use"}
            </h1>
            {hero.subtitle && <p className="text-base sm:text-lg text-slate-600 mt-4 leading-relaxed">{hero.subtitle}</p>}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-500 mt-5">
              {hero.last_updated && <p data-testid="terms-last-updated"><span className="font-medium text-slate-700">Last Updated:</span> {hero.last_updated}</p>}
              {hero.version && <p><span className="font-medium text-slate-700">Version:</span> {hero.version}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Body with ToC */}
      <main className="flex-1">
        <div className="container-cz py-10 sm:py-14 grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-12">
          {/* Desktop sticky ToC */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2" aria-label="Table of contents" data-testid="terms-toc-desktop">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">On this page</p>
              <ol className="space-y-0.5">
                {sections.map((s, i) => {
                  const id = `sec-${i + 1}`;
                  const on = active === id;
                  return (
                    <li key={s.id}>
                      <button onClick={() => goTo(id)} data-testid={`toc-${id}`}
                        className={`w-full text-left text-sm py-1.5 pl-3 border-l-2 transition-colors ${on ? "border-primary text-primary font-semibold" : "border-slate-100 text-slate-500 hover:text-slate-800 hover:border-slate-300"}`}>
                        <span className="tabular-nums">{i + 1}.</span> {s.heading}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </aside>

          {/* Content */}
          <div className="min-w-0">
            {/* Mobile collapsible ToC */}
            <div className="lg:hidden mb-6 border border-slate-200 rounded-xl overflow-hidden">
              <button onClick={() => setTocOpen((o) => !o)} data-testid="terms-toc-toggle"
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-50">
                <span className="inline-flex items-center gap-2"><List className="w-4 h-4" /> Contents</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${tocOpen ? "rotate-180" : ""}`} />
              </button>
              {tocOpen && (
                <ol className="p-2 divide-y divide-slate-100" data-testid="terms-toc-mobile">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <button onClick={() => goTo(`sec-${i + 1}`)} className="w-full text-left text-sm text-slate-600 py-2 px-2 hover:text-primary">
                        {i + 1}. {s.heading}
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <article className="max-w-3xl" data-testid="terms-content">
              {page.intro && (
                <div className="text-slate-600 leading-relaxed space-y-3 pb-8 mb-4 border-b border-slate-100">
                  {renderBody(page.intro).map((b, i) =>
                    b.type === "ul"
                      ? <ul key={i} className="list-disc pl-5 space-y-2 marker:text-primary">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
                      : <p key={i}>{b.text}</p>
                  )}
                </div>
              )}

              {sections.map((s, i) => {
                const id = `sec-${i + 1}`;
                return (
                  <section key={s.id} id={id} data-toc-section className="scroll-mt-24 mt-10 first:mt-0" data-testid={`terms-section-${id}`}>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#16161a] mb-4 pb-2 border-b border-slate-100">
                      <span className="text-primary">{i + 1}.</span> {s.heading}
                    </h2>
                    {renderBody(s.body).map((b, k) =>
                      b.type === "ul" ? (
                        <ul key={k} className="list-disc pl-5 space-y-2 text-slate-600 leading-relaxed my-3 marker:text-primary">
                          {b.items.map((it, j) => <li key={j}>{it}</li>)}
                        </ul>
                      ) : (
                        <p key={k} className="text-slate-600 leading-relaxed my-3 whitespace-pre-line">{b.text}</p>
                      )
                    )}
                  </section>
                );
              })}
            </article>
          </div>
        </div>
      </main>

      <Footer settings={settings} />
    </div>
  );
}
